import * as React from "react";
import { useContext, useMemo } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import moment from "moment";
import capitalize from "lodash/capitalize";
import toLower from "lodash/toLower";
import { Trans, useTranslation } from "react-i18next";
import Timestamp from "components/common/Timestamp";
import usePluginEntities from "hooks/usePluginEntities";
import { DEFAULT_TIMERANGE } from "views/Constants";
import StreamsContext from "contexts/StreamsContext";
import { MESSAGE_LIST_WIDGET_LIMIT } from "common/components/widgetRenderers/Constants";
export const FallbackWidgetDescription = ({ error }) => {
  const { t } = useTranslation("enterprise_common");
  return /* @__PURE__ */ React.createElement("span", null, t("widget_description.error"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, error.toString()));
};
const assertUnreachable = (_ignored) => {
  throw new Error("Didn't expect to get here");
};
const isRelativeTimeRangeStartOnly = (timerange) => "range" in timerange;
const humanizeDuration = (seconds) => moment.duration(seconds, "seconds").format("d [days] h [hours] m [minutes] s [seconds]", { trim: "all", useToLocaleString: false });
const RelativeRangeDescription = ({ relativeRange }) => {
  const { t } = useTranslation("enterprise_common");
  const start = humanizeDuration(
    isRelativeTimeRangeStartOnly(relativeRange) ? relativeRange.range : relativeRange.from
  );
  const end = isRelativeTimeRangeStartOnly(relativeRange) || !relativeRange.to ? t("widget_description.now") : t("widget_description.ago", { duration: humanizeDuration(relativeRange.to) });
  return /* @__PURE__ */ React.createElement("span", null, t("widget_description.relative_range", { start, end }));
};
const TimeRangeDescription = ({ timeRange: timeRangeProp }) => {
  const { t } = useTranslation("enterprise_common");
  const timeRange = timeRangeProp != null ? timeRangeProp : DEFAULT_TIMERANGE;
  switch (timeRange.type) {
    case "relative":
      return /* @__PURE__ */ React.createElement(RelativeRangeDescription, { relativeRange: timeRange });
    case "absolute":
      return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "widget_description.absolute_range",
          components: {
            fromTimestamp: /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timeRange.from }),
            toTimestamp: /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timeRange.to })
          }
        }
      ));
    case "keyword":
      return /* @__PURE__ */ React.createElement("span", null, t("widget_description.keyword_range", { keyword: toLower(timeRange.keyword) }));
    default:
      assertUnreachable(timeRange);
  }
  return assertUnreachable(timeRange);
};
const formatStreamTitles = (streams, language) => {
  const titles = streams.map((s) => s.title);
  const locale = language === "en" ? "en-GB" : language;
  let elementIndex = 0;
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).formatToParts(titles).map((part) => {
      if (part.type === "element") {
        const stream = streams[elementIndex];
        elementIndex += 1;
        return /* @__PURE__ */ React.createElement("em", { key: stream.id }, stream.title);
      }
      return /* @__PURE__ */ React.createElement(React.Fragment, { key: `literal-${elementIndex}-${part.value}` }, part.value);
    });
  } catch {
    return streams.map((stream) => /* @__PURE__ */ React.createElement("em", { key: stream.id }, stream.title));
  }
};
const StreamsDescription = ({ streams }) => {
  const { t, i18n } = useTranslation("enterprise_common");
  if (!streams || streams.length === 0) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, t("widget_description.all_messages"));
  }
  return /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "widget_description.messages_in_streams",
      count: streams.length,
      components: { streamList: /* @__PURE__ */ React.createElement(React.Fragment, null, formatStreamTitles(streams, i18n.language)) }
    }
  );
};
const CalculatedAt = ({ date }) => /* @__PURE__ */ React.createElement(Timestamp, { dateTime: date });
const _visualizationDescription = (config) => {
  const visualization = PluginStore.exports("visualizationTypes").filter((viz) => viz.type === config.visualization)[0];
  if (!visualization) {
    throw new Error(`Unable to find visualization component for type: ${config.visualization}`);
  }
  return visualization.displayName;
};
const WidgetTypeDescription = ({ widgetPlugin, config }) => {
  const { t } = useTranslation("enterprise_common");
  switch (widgetPlugin.type) {
    case "AGGREGATION":
      return /* @__PURE__ */ React.createElement(React.Fragment, null, capitalize(_visualizationDescription(config)), " ", toLower(widgetPlugin.titleGenerator({ config })));
    case "MESSAGES":
      return /* @__PURE__ */ React.createElement(React.Fragment, null, t("widget_description.message_list", { limit: MESSAGE_LIST_WIDGET_LIMIT }));
    default:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, t("widget_description.unknown_widget"));
  }
};
const QueryDescription = ({ query }) => {
  const { query_string: queryString = "*" } = query != null ? query : {};
  return /* @__PURE__ */ React.createElement("em", null, queryString);
};
const FiltersDescription = ({ filters }) => /* @__PURE__ */ React.createElement(React.Fragment, null, filters.map(({ queryString, negation }, index) => {
  const prefix = `${index > 0 ? " AND " : ""}`;
  const query = `${negation ? "NOT " : ""}(${queryString})`;
  return `${prefix}${query}`;
}));
const WidgetDescription = ({
  calculatedAt = /* @__PURE__ */ new Date(),
  widgetConfig,
  widgetQuery,
  widgetFilters,
  widgetStreams: widgetStreamsProp,
  widgetTimerange,
  widgetType,
  hideQuery
}) => {
  var _a;
  const { t } = useTranslation("enterprise_common");
  const streams = (_a = useContext(StreamsContext)) != null ? _a : [];
  const widgetStreams = streams.filter((stream) => widgetStreamsProp == null ? void 0 : widgetStreamsProp.includes(stream.id));
  const widgetTypes = usePluginEntities("enterpriseWidgets");
  const widgetPlugin = useMemo(
    () => widgetTypes.find((w) => w.type.toUpperCase() === widgetType.toUpperCase()),
    [widgetType, widgetTypes]
  );
  if (!widgetPlugin) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, t("widget_description.no_description"));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "widget_description.summary",
      components: {
        widgetType: /* @__PURE__ */ React.createElement(WidgetTypeDescription, { widgetPlugin, config: widgetConfig }),
        streams: /* @__PURE__ */ React.createElement(StreamsDescription, { streams: widgetStreams }),
        timeRange: /* @__PURE__ */ React.createElement(TimeRangeDescription, { timeRange: widgetTimerange })
      }
    }
  ), !hideQuery && /* @__PURE__ */ React.createElement(React.Fragment, null, " ", /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: widgetFilters && widgetFilters.length > 0 ? "widget_description.query_with_filters" : "widget_description.query",
      components: {
        query: /* @__PURE__ */ React.createElement(QueryDescription, { query: widgetQuery }),
        filters: /* @__PURE__ */ React.createElement("em", null, /* @__PURE__ */ React.createElement(FiltersDescription, { filters: widgetFilters }))
      }
    }
  )), " ", /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "widget_description.calculated_at",
      components: { calculatedAt: /* @__PURE__ */ React.createElement(CalculatedAt, { date: calculatedAt }) }
    }
  ));
};
export default WidgetDescription;
